import React from 'react';
import { FormGroup, Input, InputGroup, InputGroupText, Row } from 'reactstrap';
import ValidatedSelect, {
    ReactSelectStyles,
} from '../common/forms/ValidatedSelect';
import { FormCheckbox, FormLabel } from '../common/forms/FormElements';
import _ from 'lodash';
import cls from 'classnames';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import JobResourceOverrideList from './JobResourceOverrideList';
import UpdatingEquipmentTypeTable from './UpdatingEquipmentTypeTable';
import { MaxResourceCount } from '../longTermSchedule/LongTermSchedule';

export default class JobResourceMultiSelect extends React.Component {
    extractEquipmentTypeIdsFromJobResources = (managedJobResources) =>
        managedJobResources
            .filter(
                (mjr) =>
                    mjr.equipmentTypeId && this.getJobResourceValue(mjr) > 0
            )
            .map((mjr) => mjr.equipmentTypeId);

    constructor(props) {
        super(props);

        const managedJobResources = (props.jobResources || []).filter((jr) =>
            Boolean(jr.equipmentTypeId)
        );

        let state = {
            isValid: true,
            originalJobResources: managedJobResources,
            managedJobResources,
            uuid: uuid(),
            entityId: props.entityId || null,
            lowboyResources: props.lowboyResources || [],
        };

        state.managedEquipmentTypeIds = managedJobResources
            .filter((mjr) => this.getJobResourceValue(mjr) > 0)
            .map((mjr) => mjr.equipmentTypeId);

        this.state = state;
    }

    onInternalJobResourceChange = (selectedJobResources) => {
        let {
            managedJobResources,
            managedEquipmentTypeIds,
            originalJobResources,
        } = this.state;

        managedEquipmentTypeIds = selectedJobResources.map((jr) => jr.id);

        managedJobResources = managedJobResources.filter((mjr) =>
            selectedJobResources.some((et) => et.id == mjr.equipmentTypeId)
        );

        const newManagedEquipmentTypes = selectedJobResources.filter(
            (et) =>
                !managedJobResources.some((mjr) => mjr.equipmentTypeId == et.id)
        );

        if (newManagedEquipmentTypes && newManagedEquipmentTypes.length) {
            managedJobResources = managedJobResources.concat(
                originalJobResources.find((ojr) =>
                    newManagedEquipmentTypes
                        .map((met) => met.id)
                        .includes(ojr.equipmentTypeId)
                )
            );
        }

        const isValid = this.areEntriesValid(managedJobResources);

        this.setState(
            { isValid, managedJobResources, managedEquipmentTypeIds },
            () => {
                this.signalUpdate();
            }
        );
    };

    areEntriesValid = (managedJobResources) => {
        const { dayName } = this.props;

        return managedJobResources.every((mjr) => mjr[dayName] > 0);
    };

    signalUpdate = () => {
        const { managedJobResources, isValid, managedEquipmentTypeIds } =
            this.state;

        const { onManagedJobResourceCountChange, dayName } = this.props;

        onManagedJobResourceCountChange({
            managedJobResources: managedJobResources.filter((mjr) =>
                managedEquipmentTypeIds.includes(mjr.equipmentTypeId)
            ),
            isValid,
            dayName,
        });
    };

    onInternalCountChange = (dataContainer, e) => {
        const { managedJobResources } = this.state;
        const { dayName } = this.props;

        const { equipmentTypeId } = dataContainer;

        const index = _.findIndex(
            managedJobResources,
            (mjr) => mjr.equipmentTypeId == equipmentTypeId
        );

        const newCount = !isNaN(e.target.value) ? Number(e.target.value) : 0;

        managedJobResources[index][dayName] = newCount;

        const isValid = this.areEntriesValid(managedJobResources);

        this.setState({ managedJobResources, isValid }, () => {
            this.signalUpdate();
        });
    };

    onLowboyResourceChange = (dataContainer, e) => {
        const { equipmentTypeId, dayOfDate } = dataContainer;
        const checked = e.target.checked;

        let { lowboyResources } = this.state;

        if (checked) {
            const lowboyResource = {
                equipmentTypeId,
                date: dayOfDate,
            };

            lowboyResources.push(lowboyResource);
        } else {
            lowboyResources = lowboyResources.filter(
                (lr) =>
                    lr.equipmentTypeId != equipmentTypeId ||
                    lr.date != dayOfDate
            );
        }

        this.setState({ lowboyResources }, () => {
            const { onManagedLowboyResourceChange } = this.props;

            onManagedLowboyResourceChange(lowboyResources);
        });
    };

    componentDidUpdate(prevProps) {
        const { entityId } = this.state;

        if (
            !this.props.entityEquipmentTypes ||
            (prevProps.entityEquipmentTypes.length ==
                this.props.entityEquipmentTypes.length &&
                this.props.entityId == entityId)
        ) {
            return;
        }

        const { entityEquipmentTypes } = this.props;

        const managedEquipmentTypeIds =
            this.extractEquipmentTypeIdsFromJobResources(entityEquipmentTypes);

        this.setState({
            managedJobResources: entityEquipmentTypes,
            entityId: this.props.entityId,
            equipmentTypes: this.props.equipmentTypes,
            lowboyResources: this.props.lowboyResources,
            managedEquipmentTypeIds,
        });
    }

    getJobResourceValue = (jobResource) => {
        const { dayName } = this.props;

        if (!jobResource || !dayName) {
            return null;
        }

        return jobResource[dayName.toLowerCase()];
    };

    tooMuchDefined = (jobResource) => {
        const { dayName } = this.props;

        if (!jobResource || !dayName) {
            return null;
        }

        return jobResource[dayName.toLowerCase()] > MaxResourceCount;
    };

    onJobResourceStartTimesChanged = (jobResource, times) => {
        const { dayOfDate, signalChange } = this.props;

        const workDate = dayOfDate ? moment(dayOfDate) : moment();
        const normalizedOverrides = (times || [])
            .filter((timeEntry) => moment(timeEntry.value, 'HH:mm', true).isValid())
            .map((timeEntry) => {
                const parsedTime = moment(timeEntry.value, 'HH:mm', true);

                return {
                    id:
                        !timeEntry.id ||
                        timeEntry.id.constructor === String
                            ? null
                            : timeEntry.id,
                    date: workDate.clone().startOf('day').toDate(),
                    startTime: workDate
                        .clone()
                        .hour(parsedTime.hour())
                        .minute(parsedTime.minute())
                        .second(0)
                        .millisecond(0)
                        .toDate(),
                    arcGisOriginId: timeEntry.startLocationId ?? null,
                    arcGisDestinationId: timeEntry.endLocationId ?? null,
                    roadName:
                        timeEntry.startLocationId || timeEntry.endLocationId
                            ? ''
                            : timeEntry.startLocationText ?? '',
                };
            });

        jobResource.jobResourceOverrides = normalizedOverrides;

        if (signalChange) {
            signalChange();
        }
    };

    getOrderedEntityEquipmentTypes = () => {
        const { managedJobResources, managedEquipmentTypeIds } = this.state;

        if (!managedJobResources) {
            return [];
        }

        const { equipmentTypes } = this.props;

        const filteredJobResources = managedJobResources.filter((mjr) =>
            managedEquipmentTypeIds.includes(mjr.equipmentTypeId)
        );

        filteredJobResources.sort((a, b) => {
            const contractPhaseA = equipmentTypes.find(
                (cp) => a.equipmentTypeId == cp.id
            );
            const contractPhaseB = equipmentTypes.find(
                (cp) => b.equipmentTypeId == cp.id
            );

            if (contractPhaseA && !contractPhaseB) {
                return -1;
            }

            const externalA = (contractPhaseA || {}).externalId || null;
            const externalB = (contractPhaseB || {}).externalId || null;

            if (
                (!contractPhaseA && !contractPhaseB) ||
                externalA == externalB
            ) {
                return 0;
            }

            return externalA < externalB ? -1 : 1;
        });

        return filteredJobResources;
    };

    render() {
        const {
            equipmentTypes,
            labelText,
            readOnly,
            equipmentTypeCounts,
            dayOfDate,
            equipmentTypeIdsRequiringMoves,
            onResourcesUpdated,
            job,
            dayName,
            saving,
            hideInlineSave,
            signalChange,
            disabled,
        } = this.props;

        const { uuid, managedEquipmentTypeIds, lowboyResources } = this.state;

        const arcGisLocations = this.props.arcGisLocations || [];
        const workDateLabel = dayOfDate
            ? moment(dayOfDate).format('MM/DD/YYYY')
            : '';

        const filteredJobResources = this.getOrderedEntityEquipmentTypes();

        return (
            <FormGroup className="job-resource-multi-select">
                {labelText && (
                    <FormLabel
                        htmlFor="entityEquipmentTypes"
                        text={labelText}
                    />
                )}

                {!readOnly && (
                    <ValidatedSelect
                        name="entityEquipmentTypes"
                        options={equipmentTypes}
                        getOptionValue={(et) => et.id}
                        getOptionLabel={(et) =>
                            `${et.externalId} - ${et.description}`
                        }
                        isMulti
                        value={
                            equipmentTypes.filter((et) =>
                                managedEquipmentTypeIds.includes(et.id)
                            ) ?? ''
                        }
                        onChange={this.onInternalJobResourceChange}
                        hideClear
                        menuPortalTarget={
                            typeof document !== 'undefined'
                                ? document.body
                                : null
                        }
                        menuPosition="fixed"
                        styles={{
                            ...ReactSelectStyles,
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                        disabled={disabled}
                    />
                )}

                {!readOnly &&
                    filteredJobResources.map((mjr, i) => {
                        const equipmentType = equipmentTypes.find(
                            (et) => et.id == mjr.equipmentTypeId
                        );

                        const equipmentTypeCount = equipmentTypeCounts.find(
                            (etc) =>
                                etc.equipmentTypeId == mjr.equipmentTypeId &&
                                moment(etc.forDate).isSame(dayOfDate, 'day')
                        );

                        if (!equipmentType || !equipmentTypeCount) {
                            return null;
                        }

                        const resourceCount =
                            this.getJobResourceValue(mjr) || 0;

                        return (
                            <JobResourceOverrideList
                                key={`${uuid}-${i}`}
                                max={resourceCount}
                                arcGisLocations={arcGisLocations}
                                jobStartTime={this.props.jobStartTime}
                                jobResourceOverrides={mjr.jobResourceOverrides}
                                onOverridesChange={this.onJobResourceStartTimesChanged.bind(
                                    this,
                                    mjr
                                )}
                                disabled={this.props.disabled}
                                resourceLabel={equipmentType.description}
                                workDateLabel={workDateLabel}
                            >
                                {(addButton) => (
                                    <InputGroup className="mt-2">
                                        <InputGroupText>
                                            {equipmentType.description}
                                        </InputGroupText>

                                        <Input
                                            value={this.getJobResourceValue(
                                                mjr
                                            )}
                                            className={cls({
                                                invalid:
                                                    !this.getJobResourceValue(
                                                        mjr
                                                    ) ||
                                                    this.tooMuchDefined(mjr),
                                            })}
                                            onChange={this.onInternalCountChange.bind(
                                                this,
                                                {
                                                    equipmentTypeId:
                                                        mjr.equipmentTypeId,
                                                }
                                            )}
                                            inputMode="numeric"
                                            invalid={
                                                !this.getJobResourceValue(
                                                    mjr
                                                ) || this.tooMuchDefined(mjr)
                                            }
                                        />

                                        <InputGroupText
                                            className={cls({
                                                'text-danger':
                                                    equipmentTypeCount.availableCount ==
                                                    0,
                                            })}
                                        >
                                            {equipmentTypeCount.availableCount ==
                                                0 && (
                                                <FontAwesomeIcon
                                                    icon={faTriangleExclamation}
                                                    className="mr-2"
                                                />
                                            )}
                                            {equipmentTypeCount.availableCount}{' '}
                                            available
                                        </InputGroupText>

                                        {addButton}

                                        {equipmentTypeIdsRequiringMoves.includes(
                                            mjr.equipmentTypeId
                                        ) && (
                                            <Row className="w-100 ml-2 mt-2">
                                                <FormCheckbox
                                                    id={`chk-${uuid}-${i}-${mjr.equipmentTypeId}`}
                                                    name={`chk-${uuid}-${i}-${mjr.equipmentTypeId}`}
                                                    checked={lowboyResources.some(
                                                        (lr) =>
                                                            lr.equipmentTypeId ==
                                                                mjr.equipmentTypeId &&
                                                            lr.date == dayOfDate
                                                    )}
                                                    labelText={`Schedule Move for ${equipmentType.description}?`}
                                                    onChange={this.onLowboyResourceChange.bind(
                                                        this,
                                                        {
                                                            equipmentTypeId:
                                                                mjr.equipmentTypeId,
                                                            dayOfDate,
                                                        }
                                                    )}
                                                />
                                            </Row>
                                        )}
                                    </InputGroup>
                                )}
                            </JobResourceOverrideList>
                        );
                    })}

                {readOnly && (
                    <UpdatingEquipmentTypeTable
                        filteredJobResources={filteredJobResources}
                        equipmentTypes={equipmentTypes}
                        arcGisLocations={arcGisLocations}
                        lowboyResources={lowboyResources}
                        equipmentTypeIdsRequiringMoves={
                            equipmentTypeIdsRequiringMoves
                        }
                        dayOfDate={dayOfDate}
                        onResourcesUpdated={onResourcesUpdated}
                        job={job}
                        dayName={dayName}
                        saving={saving}
                        hideInlineSave={hideInlineSave}
                        signalChange={signalChange}
                    />
                )}
            </FormGroup>
        );
    }
}

